import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import useProcedureStore from '../stores/procedureStore';
import useDepartmentStore from '../stores/departmentStore';
import AdminPageHeader from '../components/common/AdminPageHeader';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import { ConfirmationModal } from '../components/common';
import { SPECIMEN_TYPES, IMAGING_TYPES, BUILTIN_TYPES } from '../constants/lab';
import { ORG_WIDE_FILTER, filterByDeptMembership } from '../utils/deptMembershipFilter';
import './ProcedureCatalogPage.css';

// Lab tests live in the shared procedure catalog under category="lab"; this page
// reuses the procedure store + catalog styling, filtered to lab, with the
// lab-specific fields (code / specimen / send-out). Payment is "before" in v1.
// The "Type" field (stored as `specimen_type`) covers pathology samples AND
// imaging modalities — SPECIMEN_TYPES / IMAGING_TYPES are the single source of
// truth in src/constants/lab.js (the technician queue reads the same set). An
// org's own values self-build into a "Custom" group from anything entered via
// "Other (specify)".
const OTHER_SENTINEL = '__other__';

const BLANK = {
  name: '',
  code: '',
  specimen_type: '',
  default_cost: '',
  gst_rate: 0,
  skip_payment: false,
  internal_flag: false,
  receptionist_allowed: true,
  notes: '',
};

const FLAGS = [
  ['receptionist_allowed', 'Receptionist can add'],
  ['skip_payment', 'Skip payment'],
  ['internal_flag', 'Send-out (external lab)'],
];

function validate(row) {
  const e = {};
  if (!row.name || !row.name.trim()) e.name = 'Required';
  if (row.default_cost === '' || row.default_cost === null || row.default_cost === undefined) {
    e.default_cost = 'Required';
  } else if (Number.isNaN(Number(row.default_cost)) || Number(row.default_cost) < 0) {
    e.default_cost = 'Must be 0 or more';
  }
  return e;
}

export default function LabCatalogPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();

  const {
    procedures,
    isLoading,
    error,
    fetchProcedures,
    createProcedure,
    updateProcedure,
    deactivateProcedure,
    clearError,
    setProcedureDepartments,
  } = useProcedureStore();

  // A5 — org departments for the "Visible to" tag field (mirrors A4's
  // ProcedureCatalogPage). Only ACTIVE departments are offered.
  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => d.active !== false)
      .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name })),
    [departments],
  );
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of activeDepts) m[d.dept_id] = d.name;
    return m;
  }, [activeDepts]);

  const [form, setForm] = useState(null); // null | { mode:'add' } | { mode:'edit', id, category }
  const [row, setRow] = useState({ ...BLANK });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [banner, setBanner] = useState(null);
  const [otherMode, setOtherMode] = useState(false); // "Other (specify)" Type entry active
  // B5 (bug 3) — MEMBERSHIP list dept-filter (client-side over already-loaded
  // lab tests — no new fetch on change). Empty = all; a dept = only rows tagged
  // to it; "Org-wide" = only untagged.
  const [deptFilter, setDeptFilter] = useState('');
  // A5 — dept-tag picker selection for the current form (add or edit), seeded
  // from the row's department_ids on edit. Saved as part of the same Save click.
  const [deptIds, setDeptIds] = useState([]);

  useEffect(() => {
    fetchProcedures();
    fetchDepartments();
  }, [fetchProcedures, fetchDepartments]);

  const labTests = useMemo(() => {
    let list = procedures.filter((p) => p.category === 'lab');
    if (!showInactive) list = list.filter((p) => p.active !== false);
    list = filterByDeptMembership(list, deptFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.code || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [procedures, search, showInactive, deptFilter]);

  // Per-org "Custom" Type group: distinct specimen_type already used on the org's
  // lab tests that isn't a built-in (case-insensitive). Self-builds from "Other".
  const customTypes = useMemo(() => {
    const builtin = new Set(BUILTIN_TYPES.map((t) => t.toLowerCase()));
    const seen = new Set();
    const out = [];
    for (const p of procedures) {
      if (p.category !== 'lab') continue;
      const t = (p.specimen_type || '').trim();
      const lower = t.toLowerCase();
      if (!t || builtin.has(lower) || seen.has(lower)) continue;
      seen.add(lower);
      out.push(t);
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [procedures]);

  // Type-field select state: show the matching option when the value is known;
  // otherwise it's a brand-new custom value → the "Other (specify)" input.
  const typeValue = row.specimen_type || '';
  const typeKnown =
    typeValue !== '' &&
    [...BUILTIN_TYPES, ...customTypes].some((o) => o.toLowerCase() === typeValue.toLowerCase());
  const showTypeOther = otherMode || (typeValue !== '' && !typeKnown);
  const typeSelectValue = showTypeOther ? OTHER_SENTINEL : typeValue;

  const openAdd = () => {
    setRow({ ...BLANK });
    setErrors({});
    setOtherMode(false);
    setDeptIds([]);
    setForm({ mode: 'add' });
  };
  const openEdit = (p) => {
    setRow({ ...BLANK, ...p });
    setErrors({});
    setOtherMode(false);
    // Seed the dept-tag editor from the row's current tags (active edges).
    setDeptIds(Array.isArray(p.department_ids) ? [...p.department_ids] : []);
    setForm({ mode: 'edit', id: p.procedure_id, category: p.category });
  };
  const close = () => {
    setForm(null);
    setErrors({});
    setDeptIds([]);
  };
  const change = (k, v) => setRow((r) => ({ ...r, [k]: v }));
  const toggleDept = (deptId) => {
    setDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((d) => d !== deptId) : [...prev, deptId]
    );
  };

  const submit = async () => {
    const e = validate(row);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    const body = {
      ...row,
      default_cost: Number(row.default_cost),
      gst_rate: Number(row.gst_rate) || 0,
    };
    for (const k of ['code', 'specimen_type', 'notes']) {
      if (body[k] === '') delete body[k];
    }
    // `recurring` was removed from the backend catalog (extra=forbid → 422). An
    // edited row spread from the backend may still carry it — strip it (lab tests
    // POST/PATCH the same /procedures endpoint as procedures).
    delete body.recurring;
    let result;
    if (form.mode === 'add') {
      result = await createProcedure({ ...body, category: 'lab' });
    } else {
      const patch = { ...body };
      delete patch.procedure_id;
      delete patch.organization_id;
      delete patch.category;
      delete patch.created_at;
      delete patch.updated_at;
      // Read-only enrichment fields from the list response — NOT part of
      // UpdateClinicProcedureRequest (extra=forbid); sending them 422s.
      // Departments are saved via the separate setProcedureDepartments call below.
      delete patch.owner_scope;
      delete patch.department_ids;
      result = await updateProcedure(form.category, form.id, patch);
    }
    if (!result.success) {
      setBusy(false);
      setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      return;
    }
    // A5 — tag departments as part of the SAME Save click. Create: only when
    // ≥1 selected (using the server-assigned procedure_id from the create
    // response). Edit: always, so a cleared selection reverts to org-wide.
    const isAdd = form.mode === 'add';
    const procedureId = isAdd ? result.data?.procedure_id : form.id;
    if (!isAdd || deptIds.length > 0) {
      if (!procedureId) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'Lab test created, but departments could not be saved — edit it to set departments.' });
        close();
        return;
      }
      const deptResult = await setProcedureDepartments('lab', procedureId, deptIds);
      setBusy(false);
      if (!deptResult.success) {
        setBanner({ kind: 'err', msg: `Lab test ${isAdd ? 'created' : 'updated'}, but departments were not saved: ${deptResult.error || 'unknown error'}` });
        return;
      }
    } else {
      setBusy(false);
    }
    close();
    setBanner({ kind: 'ok', msg: isAdd ? 'Lab test created' : 'Lab test updated' });
  };

  const askDeactivate = (p) => {
    setConfirm({
      title: 'Deactivate lab test?',
      message: `"${p.name}" will be hidden from new orders. Historical orders keep working. Re-activate later by editing and toggling Active.`,
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateProcedure(p.category, p.procedure_id);
        setBanner(
          r.success
            ? { kind: 'ok', msg: 'Lab test deactivated' }
            : { kind: 'err', msg: r.error || 'Deactivate failed' }
        );
      },
    });
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const inputCls =
    'w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const labelCls =
    'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  // Permission gate — all hooks above; safe to early-return now. Redirect home
  // (not /access-denied, whose only button logs out) — a missing feature
  // permission in an authenticated session shouldn't push toward logout.
  if (!hasPermission('MANAGE_LAB_CATALOG')) {
    return <Navigate to={ROUTES.ORG} replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🧪 Lab Catalog
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Lab tests doctors advise and technicians run. Payment is collected before the test (v1).
            </p>
          </div>
          <button className="pcp-btn pcp-btn-primary" onClick={openAdd} disabled={!!form}>
            + Add Lab Test
          </button>
        </div>
        <FeesBillingTabs active="lab" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        {form && (
          <div className="pcp-add-wrap">
            <div className="pcp-add-title">
              {form.mode === 'add' ? 'New Lab Test' : `Edit · ${row.name}`}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2">
                <label className={labelCls}>Test name *</label>
                <input
                  className={inputCls}
                  value={row.name}
                  onChange={(e) => change('name', e.target.value)}
                  placeholder="Complete Blood Count (CBC)"
                />
                {errors.name && <div className="text-xs text-rose-600 mt-1">{errors.name}</div>}
              </div>
              <div>
                <label className={labelCls}>Code</label>
                <input
                  className={inputCls}
                  value={row.code || ''}
                  onChange={(e) => change('code', e.target.value)}
                  placeholder="LAB-CBC"
                />
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <select
                  className={inputCls}
                  value={typeSelectValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === OTHER_SENTINEL) {
                      setOtherMode(true);
                      change('specimen_type', '');
                    } else {
                      setOtherMode(false);
                      change('specimen_type', val);
                    }
                  }}
                >
                  <option value="">—</option>
                  <optgroup label="Specimen">
                    {SPECIMEN_TYPES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Imaging / Modality">
                    {IMAGING_TYPES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </optgroup>
                  {customTypes.length > 0 && (
                    <optgroup label="Custom">
                      {customTypes.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </optgroup>
                  )}
                  <option value={OTHER_SENTINEL}>Other (specify)…</option>
                </select>
                {showTypeOther && (
                  <input
                    className={inputCls}
                    type="text"
                    style={{ marginTop: '0.375rem' }}
                    value={row.specimen_type || ''}
                    onChange={(e) => change('specimen_type', e.target.value)}
                    placeholder="e.g. PET-CT, Fibroscan"
                    maxLength={40}
                  />
                )}
              </div>
              <div>
                <label className={labelCls}>Cost (₹) *</label>
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  value={row.default_cost}
                  onChange={(e) => change('default_cost', e.target.value)}
                />
                {errors.default_cost && (
                  <div className="text-xs text-rose-600 mt-1">{errors.default_cost}</div>
                )}
              </div>
              <div>
                <label className={labelCls}>GST</label>
                <select
                  className={inputCls}
                  value={row.gst_rate}
                  onChange={(e) => change('gst_rate', e.target.value)}
                >
                  {[0, 5, 12, 18, 28].map((g) => (
                    <option key={g} value={g}>{g}%</option>
                  ))}
                </select>
              </div>
              {form.mode === 'edit' && (
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 mt-5">
                  <input
                    type="checkbox"
                    checked={row.active !== false}
                    onChange={(e) => change('active', e.target.checked)}
                  />
                  Active
                </label>
              )}
            </div>

            <div className="flex flex-wrap gap-4 mt-3">
              {FLAGS.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input type="checkbox" checked={!!row[k]} onChange={(e) => change(k, e.target.checked)} />
                  {lbl}
                </label>
              ))}
            </div>

            {/* A5 — "Visible to" departments (optional, multi — mirrors A4's
                ProcedureCatalogPage). Hidden when the org has no active
                departments. Empty selection = org-wide (implicit). Saved as
                part of the same Save click as everything else. */}
            {activeDepts.length > 0 && (
              <div className="mt-3">
                <label className={labelCls}>
                  Visible to{' '}
                  <span className="text-xs font-normal normal-case text-slate-400 dark:text-slate-500">
                    (optional, multi — leave empty for org-wide)
                  </span>
                </label>
                <div className="flex flex-wrap gap-4">
                  {activeDepts.map((d) => (
                    <label key={d.dept_id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                      <input
                        type="checkbox"
                        checked={deptIds.includes(d.dept_id)}
                        onChange={() => toggleDept(d.dept_id)}
                      />
                      {d.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button className="pcp-btn pcp-btn-primary" onClick={submit} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button className="pcp-btn pcp-btn-secondary" onClick={close} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="pcp-filter-bar">
          <input
            type="text"
            className="pcp-search"
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          {/* A5 / B5 (bug 3) — MEMBERSHIP dept-filter. Hidden on a dark org. */}
          {activeDepts.length > 0 && (
            <select
              className={inputCls}
              style={{ width: 'auto' }}
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="">All departments</option>
              <option value={ORG_WIDE_FILTER}>Org-wide</option>
              {activeDepts.map((d) => (
                <option key={d.dept_id} value={d.dept_id}>{d.name}</option>
              ))}
            </select>
          )}
          <span className="pcp-count">
            {labTests.length} lab test{labTests.length === 1 ? '' : 's'}
          </span>
        </div>

        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => {
                clearError();
                fetchProcedures();
              }}
            >
              Retry
            </button>
          </div>
        ) : labTests.length === 0 ? (
          <div className="pcp-state">🧪 No lab tests yet. Click + Add Lab Test to create the first one.</div>
        ) : (
          <table className="pcp-table">
            <thead>
              <tr>
                <th>Test</th>
                <th>Code</th>
                <th>Type</th>
                <th>Cost</th>
                <th>GST</th>
                <th>Flags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {labTests.map((p) => (
                <tr key={p.procedure_id} className={p.active === false ? 'pcp-row pcp-row-inactive' : 'pcp-row'}>
                  <td>
                    {/* Internal catalog id (proc_…) hidden — staff identify a
                        lab test by its CODE column, not the plumbing id. */}
                    <div className="pcp-name">{p.name}</div>
                  </td>
                  <td>{p.code || '—'}</td>
                  <td>{p.specimen_type || '—'}</td>
                  <td className="pcp-cost">₹{Number(p.default_cost || 0).toFixed(2)}</td>
                  <td>{p.gst_rate ?? 0}%</td>
                  <td>
                    <div className="pcp-chips">
                      {p.receptionist_allowed && <span className="pcp-chip">recept</span>}
                      {p.skip_payment && <span className="pcp-chip">skip-pay</span>}
                      {p.internal_flag && <span className="pcp-chip">send-out</span>}
                      {p.active === false && <span className="pcp-chip pcp-chip-inactive">Deactivated</span>}
                      {/* A5 — current department tags (active edges). No tag = org-wide. */}
                      {(p.department_ids || []).length > 0 && (
                        <span className="pcp-depts-caption">Visible to:</span>
                      )}
                      {(p.department_ids || []).map((did) => (
                        <span
                          key={did}
                          className="pcp-chip"
                          style={{
                            background: isDark ? '#3730a3' : '#e0e7ff',
                            color: isDark ? '#e0e7ff' : '#3730a3',
                            borderColor: isDark ? '#4f46e5' : '#a5b4fc',
                          }}
                        >
                          🏷 {deptNameById[did] || did}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="pcp-actions">
                      <button className="pcp-btn pcp-btn-link" onClick={() => openEdit(p)}>Edit</button>
                      {p.active !== false && (
                        <button
                          className="pcp-btn pcp-btn-link pcp-btn-danger-link"
                          onClick={() => askDeactivate(p)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>

      {confirm && (
        <ConfirmationModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          ctaLabel="Deactivate"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
